import cx from "classnames";
import { Fragment, useState, useEffect } from "react";

import * as StateLib from "@/Utils/StateLib";
import { BackgroundTabs } from "./BackgroundDisplay/BackgroundDisplay";
import StagingArea from "./StagingArea/StagingArea";
import { BoxSort } from "./Sort/Sort";
import { ChangesDisplay } from "./ChangesDisplay/ChangesDisplay";
import { Activity } from "@/appComponents/Navigation/RightSidebar/Planning/Activity/Activity";
import {
  isNullish,
  MainNavItem,
  ToggleButtonGroup,
  RightSidebar as RightSidebarComponent,
  useToggleButtonContext,
  Badge
} from "@theorgchartdev/storybook";
import { getFeatureAccess, getHoverTipContent, UPGRADE_BADGE_PROPS } from "@/Utils/Misc";
import { InteractiveHighlightGroup } from "./HighlightsPanel/InteractiveHighlightGroup";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { ViewDisplay } from "./ViewDisplay/ViewDisplay";
import { OptimizationTabs } from "./Optimize/Optimize";
import { Dashboard } from "@/appComponents/Navigation/RightSidebar/Planning/Dashboard";
import { TalentPool } from "@/appComponents/Navigation/RightSidebar/Planning/TalentPool/TalentPool";
import { GetWSMgr } from "@/Utils/StateLib";
import { FilterPanel } from "./FilterPanel";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useTopic } from "@/hooks/useTopic";
import { Grouping } from "./Grouping/Grouping";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumSortScope, enumUIModes, kROUser } from "@coreapp/constants";
import { gUpgradeIndicatorIcon } from "@coreapp/whitelabel/brand";
import { QSortConfig } from "@coreapp/CoreApp/QSortConfig";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { isInBoxStyleLibraryEditor } from "@coreapp/utils/isInBoxStyleLibraryEditor";
import { isInPlanning } from "@coreapp/utils/isInPlanning";
import { EmployeeList } from "./Planning/EmployeeList/EmployeeList";
import { enumFeaturesList } from "@coreapp/constants";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { getPlanConflictCount } from "@/appComponents/Blocks/Composites/ConflictDetectionAlert/ConflictDetectionAlert";
import {
  PlanningBoundary,
  DocumentBoundary
} from "@/appComponents/Blocks/Utils/StateBoundary/StateBoundary";
import BoxStyles from "./BoxStyles/BoxStyles";
import { FunctionalStatementPanel } from "./FunctionalStatementPanel/FunctionalStatementPanel";
import { ChartNavigatorPanel } from "./ChartNavigatorPanel/ChartNavigatorPanel";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

export const enumSidebarMode = {
  filters: {
    value: "filters",
    icon: "filter",
    header: "Filters",
    hovertip: "filterhelp",
    settingsTooltip: "Filter behavior",
    profileFlag: "showSidebarFilters"
  },
  highlights: {
    value: "highlights",
    icon: "highlighter-line",
    header: "Highlights",
    hovertip: "Highlights detail",
    profileFlag: "showSidebarHighlights"
  },
  background: {
    value: "background",
    icon: "background_",
    header: "Background",
    hovertip: "Background detail",
    profileFlag: "showSidebarBackground"
  },
  boxlibrary: {
    value: "boxlibrary",
    icon: "books",
    header: "Box Styles",
    hovertip: "Box style library"
  },
  view: {
    value: "view",
    icon: "eye",
    header: "View rules",
    hovertip: "View rules"
  },
  sort: {
    value: "sort",
    icon: "arrow-down-a-z",
    header: "Sort",
    hovertip: "boxsort"
  },
  optimize: {
    value: "optimize",
    icon: "bolt",
    header: "Optimize",
    hovertip: "optimizepage"
  },
  staging: {
    value: "staging",
    icon: "staging",
    header: "Staging",
    hovertip: "Staging detail"
  },
  changes: {
    value: "changes",
    icon: "clipboard-check",
    header: "Changes",
    hovertip: "Changes detail"
  },
  dashboard: {
    value: "dashboard",
    icon: "gauge-max",
    header: "Dashboard",
    hovertip: "Dashboard"
  },
  employeeList: {
    value: "employeeList",
    icon: "users",
    header: "Employee List",
    hovertip: "Employee List",
    profileFlag: "showSidebarEmployeeList"
  },
  talentpool: {
    value: "talentpool",
    icon: "briefcase-arrow-right",
    header: "Talent Pool",
    hovertip: "Talent pool"
  },
  planningActivity: {
    value: "planningActivity",
    icon: "clipboard-check",
    header: "Changes",
    hovertip: "Changes detail"
  },
  grouping: {
    value: "grouping",
    icon: "grouping",
    header: "Grouping",
    hovertip: "Grouping Detail"
  },
  functionalStatement: {
    value: "functionalStatement",
    icon: "chart-user-regular",
    header: "Functional Statement",
    hovertip: "View Functional Statement",
    profileFlag: "showFunctionalStatement"
  },
  chartNavigator: {
    value: "chartNavigator",
    icon: "sitemap",
    header: "Navigator",
    hovertip: "Navigate to a unit",
    profileFlag: "showChartNavigator"
  }
};

const chartModes = [
  enumSidebarMode.employeeList,
  enumSidebarMode.filters,
  enumSidebarMode.highlights,
  enumSidebarMode.background,
  enumSidebarMode.boxlibrary,
  enumSidebarMode.view,
  enumSidebarMode.grouping
];

const editModes = [
  ...chartModes,
  enumSidebarMode.sort,
  enumSidebarMode.optimize,
  enumSidebarMode.staging,
  enumSidebarMode.changes
];

const planningModes = [
  enumSidebarMode.dashboard,
  enumSidebarMode.planningActivity,
  enumSidebarMode.talentpool,
  ...chartModes
];

const reportModes = [enumSidebarMode.filters];

const backgroundModes = [enumSidebarMode.background];
const ROModes = [enumSidebarMode.filters, enumSidebarMode.highlights, enumSidebarMode.background];

export const applyDocumentTypeFiltering = (modes, doc) => {
  if (!doc) return modes;

  const isPositionDocument = doc.IsPositionDocument();

  if (isPositionDocument) {
    const excludedForPositions = [
      enumSidebarMode.staging.value,
      enumSidebarMode.grouping.value,
      enumSidebarMode.talentpool.value
    ];

    return modes.filter(mode => !excludedForPositions.includes(mode.value));
  } else {
    return modes.filter(mode => mode.value !== enumSidebarMode.employeeList.value);
  }
};

export const hasPermissionForMode = (permissions, mode) => {
  // Single source of truth for mode visibility. UI-profile gating lives here too:
  // a mode with a profileFlag only appears when that flag is enabled, on top of
  // any license/permission logic below. Consumers call this and nothing else.
  if (mode.profileFlag && !permissions[mode.profileFlag]) return false;

  const doc = StateLib.GetDoc();
  const isPlanning = doc?.IsPlanChart();
  switch (mode.value) {
    case enumSidebarMode.filters.value:
      if (!permissions.showPricingAndPackaging) {
        return permissions.canAccessSavedFilters || permissions.canAccessInteractiveFilters;
      }
      return true;
    case enumSidebarMode.highlights.value:
      if (!permissions.showPricingAndPackaging) {
        return permissions.canAccessSavedHighlights || permissions.canAccessInteractiveHighlights;
      }
      return true;
    case enumSidebarMode.view.value:
      return permissions.canAccessTemplates;
    case enumSidebarMode.boxlibrary.value:
      return permissions.canAccessBoxLibrary;
    case enumSidebarMode.background.value:
      return permissions.canAccessBackground;
    case enumSidebarMode.grouping.value:
      if (!permissions.showPricingAndPackaging) return permissions.canAccessGrouping;
      if (permissions.showPricingAndPackaging) {
        if (permissions.canEditDocuments) {
          //for showing upgrade popover if grouping is not enabled
          if (!getFeatureAccess(enumFeaturesList.grouping)) return true;
          //for showing grouping in the sidebar based on UI confi
          return permissions.canAccessGrouping;
        }
      }
      return false;
    case enumSidebarMode.talentpool.value:
      return permissions.canAccessTalentPool;
    case enumSidebarMode.planningActivity.value:
      return isPlanning && permissions.canAccessPlanningChanges;
    case enumSidebarMode.changes.value:
      if (!permissions.showPricingAndPackaging) return permissions.canAccessChanges;
      if (permissions.showPricingAndPackaging) {
        if (permissions.canEditDocuments) {
          //for showing upgrade popover if track changes is not enabled
          if (!getFeatureAccess(enumFeaturesList.trackChanges)) return true;
          return permissions.canAccessChanges;
        }
      }
      return false;
    case enumSidebarMode.staging.value:
      return permissions.canAccessStaging;
    default:
      return true;
  }
};

const SIDEBAR_MODE_EVENTS = {
  [enumSidebarMode.filters.value]: "chart_mode.filter.opened",
  [enumSidebarMode.highlights.value]: "chart_mode.highlights.opened",
  [enumSidebarMode.sort.value]: "chart_mode.box_sort.opened",
  [enumSidebarMode.optimize.value]: "chart_mode.optimize.opened",
  [enumSidebarMode.background.value]: "background_editor.background_tabs.opened",
  [enumSidebarMode.view.value]: "view_manager.views.opened",
  [enumSidebarMode.staging.value]: "chart_mode.staging.opened",
  [enumSidebarMode.boxlibrary.value]: "chart_mode.box_library.opened",
  [enumSidebarMode.changes.value]: "chart_mode.changes.opened"
};

export const RightSidebar = () => {
  const permissions = usePermissionCheck();
  const chartDocumentContext = useChartDocumentContext();

  const doc = chartDocumentContext.chartDocument.state;

  const viewManagerContext = useViewManagerContext();

  const [initialEmployeeListSearch, setInitialEmployeeListSearch] = useState("");

  useTopic("chartDocumentLoaded", () => {
    setInitialEmployeeListSearch("");
  });

  useTopic("OpenEmployeeList", state => {
    const employeeMode = enumSidebarMode.employeeList.value;
    if (isValidMode(employeeMode)) {
      setInitialEmployeeListSearch(state.employeeName);
      handleSideBarModeChange(employeeMode);
    }
  });

  const {
    sideBarMode,
    settingsOpenCtrlRef,
    settingsFloatingCtrlRef,
    sideBarExpanded,
    closeSidebar,
    handleSideBarModeChange,
    isModeDirty,
    customHeaderProps
  } = useRightSidebarContext();

  const currentModeConfig = enumSidebarMode[sideBarMode];
  const hasSettings = !!currentModeConfig?.settingsTooltip;
  const settingsTooltipMessage = appstr(currentModeConfig?.settingsTooltip ?? "Settings");

  let wsMgr = StateLib.GetWSMgr();

  const getAvailableModes = () => {
    const uiMode = Globals._uiConfig?.GetUIMode();

    let availableModes = chartModes;
    const currentFilename = wsMgr?.GetFileName(true);
    const isBackgroundFile = currentFilename ? IsBackgroundFile(currentFilename) : false;

    switch (uiMode) {
      case enumUIModes.chart:
        if (isBackgroundFile) availableModes = backgroundModes;
        if ([kROUser].includes(Globals._accountInfo.GetUserRole())) availableModes = ROModes;
        break;
      case enumUIModes.edit:
        if (isBackgroundFile) {
          availableModes = backgroundModes;
        } else {
          availableModes = editModes;
        }
        break;
      case enumUIModes.planning:
        availableModes = planningModes;
        break;
      case enumUIModes.reports:
        availableModes = reportModes;
        break;
      case enumUIModes.boxlayouteditor:
        availableModes = isInBoxStyleLibraryEditor(wsMgr) ? [enumSidebarMode.boxlibrary] : [];
        break;
    }

    availableModes = applyDocumentTypeFiltering(availableModes, doc);

    // Functional statement and navigator aren't part of the per-UI-mode arrays;
    // prepend them and let hasPermissionForMode gate visibility via their profileFlag.
    availableModes = [
      enumSidebarMode.functionalStatement,
      enumSidebarMode.chartNavigator,
      ...availableModes
    ];

    return availableModes.filter(mode => hasPermissionForMode(permissions, mode));
  };

  const isValidMode = mode => {
    let availableModes = getAvailableModes().map(item => item.value);
    return availableModes.includes(mode);
  };

  let headerTitle = enumSidebarMode[sideBarMode]?.header
    ? appstr(enumSidebarMode[sideBarMode]?.header)
    : "";

  let headerProps = {
    title: headerTitle,
    size: "medium",
    labelSize: "medium",
    headerActions: {
      closeAction: closeSidebar,
      closeActionProps: {
        tabIndex: !sideBarExpanded ? -1 : undefined,
        dataTestId: chartModeTestId("sidebar", "close-btn")
      },
      customAction: hasSettings
        ? {
            icon: "gear",
            buttonProps: {
              dataTestId: "rsb-header-settings",
              ariaLabel: settingsTooltipMessage,
              tooltipProps: {
                message: settingsTooltipMessage,
                position: "top"
              }
            },
            onClick: e => {
              settingsOpenCtrlRef.current?.setOpen(true);
              settingsFloatingCtrlRef.current?.floating.refs.setPositionReference(e.target);
            }
          }
        : undefined
    },
    ...customHeaderProps
  };

  let defaultSort = new QSortConfig();
  defaultSort.InitConfig(enumSortScope.chart);
  if (doc && doc.GetIWizData()) defaultSort = doc.GetIWizData().GetSortCriteria();

  if (!defaultSort) {
    defaultSort = new QSortConfig();
    defaultSort.InitConfig(enumSortScope.chart);
    defaultSort = doc?.GetLastSort() ?? defaultSort;
  }
  const [sortConfig, setSortConfig] = useState(defaultSort);

  useEffect(() => {
    let sortConfig = doc?.GetLastSort();
    if (!isNullish(sortConfig)) {
      setSortConfig(sortConfig);
    }
  }, [chartDocumentContext]);

  useEffect(() => {
    if (!sideBarExpanded) return;
    const eventName = SIDEBAR_MODE_EVENTS[sideBarMode];
    if (!eventName) return;
    const tracker = StateLib.GetWSMgr()?.GetUsageTracker();
    tracker?.ReportEvent(eventName);
  }, [sideBarMode, sideBarExpanded]);

  var userInfo = wsMgr && wsMgr.GetUserInfo();
  let canUpdateViews = userInfo && userInfo.CanUpdateTemplates();
  const RenderContent = () => {
    const doc = StateLib.GetDoc();
    if (!doc || !Globals._stringMgr) return null;

    // Planning-exclusive modes
    switch (sideBarMode) {
      case "dashboard":
      case "planningActivity":
      case enumSidebarMode.talentpool.value: {
        const planningComponents = {
          dashboard: Dashboard,
          planningActivity: Activity,
          [enumSidebarMode.talentpool.value]: TalentPool
        };
        const PlanComponent = planningComponents[sideBarMode];
        return (
          <PlanningBoundary key={sideBarMode}>
            <PlanComponent />
          </PlanningBoundary>
        );
      }
    }

    // All other modes
    let content;
    switch (sideBarMode) {
      case enumSidebarMode.filters.value:
        content = (
          <FilterPanel
            doc={doc}
            canShowInteractiveFilter={permissions.canAccessInteractiveFilters}
          />
        );
        break;
      case enumSidebarMode.highlights.value:
        content = (
          <InteractiveHighlightGroup
            manager={doc.GetHighlightMgr()}
            canShowInteractiveHighlights={permissions.canAccessInteractiveHighlights}
          />
        );
        break;
      case enumSidebarMode.sort.value:
        content = <BoxSort manager={sortConfig.Clone()} setter={setSortConfig} />;
        break;
      case enumSidebarMode.optimize.value:
        content = <OptimizationTabs doc={doc} />;
        break;
      case enumSidebarMode.background.value:
        content = <BackgroundTabs doc={doc} />;
        break;
      case enumSidebarMode.view.value: {
        const curView = doc.GetActiveTemplate();
        let hasChartRules = !doc.IsServerChart();
        let thisChartRules = doc.GetSelfChartRules();
        const viewPropsArr = [{ view: curView, chartRule: false }];
        if (hasChartRules) viewPropsArr.push({ view: thisChartRules, chartRule: true });
        content = <ViewDisplay viewData={viewPropsArr} />;
        break;
      }
      case enumSidebarMode.staging.value:
        content = <StagingArea />;
        break;
      case enumSidebarMode.boxlibrary.value:
        content = <BoxStyles canUpdateViews={canUpdateViews} />;
        break;
      case enumSidebarMode.changes.value:
        content = (
          <ChangesDisplay
            showChanges={true}
            showFormatting={!isInPlanning()}
            showPagination={!isInPlanning()}
          />
        );
        break;
      case enumSidebarMode.employeeList.value:
        content = <EmployeeList initialSearchQuery={initialEmployeeListSearch} />;
        break;
      case enumSidebarMode.grouping.value:
        content = <Grouping />;
        break;
      case enumSidebarMode.functionalStatement.value:
        content = <FunctionalStatementPanel />;
        break;
      case enumSidebarMode.chartNavigator.value:
        content = <ChartNavigatorPanel />;
        break;
      default:
        return null;
    }

    return <DocumentBoundary key={sideBarMode}>{content}</DocumentBoundary>;
  };

  useEffect(() => {
    if (!sideBarExpanded || isValidMode(sideBarMode)) return;
    let [firstAvailableMode, secondAvailableMode] = getAvailableModes();
    if (firstAvailableMode?.value === "background" && !isNullish(secondAvailableMode?.value))
      firstAvailableMode = secondAvailableMode;
    if (firstAvailableMode) handleSideBarModeChange(firstAvailableMode.value);
    if (Globals && Globals._accountInfo) {
      enumSidebarMode.grouping.disabled = !getFeatureAccess(enumFeaturesList.grouping);
    }
  }, [chartDocumentContext, viewManagerContext, permissions, Globals]);

  const commonBadgeProps = {
    color: "warning",
    size: "medium",
    border: false,
    overlap: "contained",
    badgeContent: appstr("Unsaved"),
    maxLength: 12
  };

  const upgradeGemBadgeProps = {
    ...UPGRADE_BADGE_PROPS,
    iconProps: { name: gUpgradeIndicatorIcon },
    badgeContent: null
  };

  const GetModeSpecificProps = mode => {
    const wsMgr = GetWSMgr();
    let props = {};
    let includeBadge = true;
    let showBadge = isModeDirty;
    let badgeProps = {};
    let showUpgradeBadge = false;
    let disabled = false;

    const applyUpgradeIndicator = () => {
      badgeProps = {
        ...upgradeGemBadgeProps,
        iconProps: { ...upgradeGemBadgeProps.iconProps, dataTestId: `rsb-${mode}-upgrade-icon` }
      };
      showBadge = true;
    };

    switch (mode) {
      case "filters": {
        const dataFiltersOn = doc?.IsChartFiltered();
        const filterMgr = doc?.GetFilterMgr();

        // Custom Filters. Always counts as 1
        const iwizData = doc?.GetIWizData();
        const customFilterApplied = doc?.IsCustomChart()
          ? iwizData?.GetFilterData() && iwizData?.GetFilterRule() != null
          : false;
        const customFilterCount = customFilterApplied ? 1 : 0;

        if (!isModeDirty && dataFiltersOn) {
          badgeProps = {
            badgeContent: (filterMgr.TotalNumberOfActiveFilters() + customFilterCount).toString(),
            color: "success",
            tooltipProps: {
              message: appstr("Number of entity applied to the chart", {
                entity: appstr("Filters").toLowerCase()
              }),
              position: "top-start"
            }
          };
          showBadge = dataFiltersOn === true;
        }
        showUpgradeBadge = permissions.shouldShowUpgradeFilters;
        if (showUpgradeBadge) {
          applyUpgradeIndicator();
        }
        break;
      }
      case "highlights": {
        const dataHighlightsOn = doc?.IsChartHighlighted();
        if (dataHighlightsOn) {
          const highlightMgr = doc?.GetHighlightMgr();
          badgeProps = {
            badgeContent: highlightMgr.GetNumberOfHighlightsApplied().toString(),
            color: "success",
            tooltipProps: {
              message: appstr("Number of entity applied to the chart", {
                entity: appstr("Highlights").toLowerCase()
              }),
              position: "top-start"
            }
          };
          showBadge = true;
        } else {
          showBadge = false;
        }
        showUpgradeBadge = permissions.shouldShowUpgradeHighlights;
        if (showUpgradeBadge) {
          applyUpgradeIndicator();
        }
        break;
      }
      case "background":
        showBadge = wsMgr?.IsBackgroundDirty() ?? false;
        break;
      case "boxlibrary":
        showBadge = false;
        break;
      case "planningActivity": {
        const planConflictCount = getPlanConflictCount() ?? 0;
        showBadge = planConflictCount > 0;
        badgeProps = {
          badgeContent: `${planConflictCount}`,
          color: "error"
        };
        break;
      }
      case "grouping":
        showBadge = doc?.IsGroupingApplied();
        badgeProps = {
          badgeContent: "!",
          color: "success"
        };
        showUpgradeBadge =
          permissions.showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.grouping);
        if (showUpgradeBadge) {
          applyUpgradeIndicator();
        }
        break;
      case "changes":
        showUpgradeBadge =
          permissions.showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.trackChanges);
        showBadge = false;
        if (showUpgradeBadge) {
          applyUpgradeIndicator();
        }
        break;
      default:
        includeBadge = false;
        break;
    }
    if (includeBadge) {
      props = {
        badgeProps: {
          ...commonBadgeProps,
          ...badgeProps,
          showBadge: showBadge
        }
      };
    } else {
      props = {
        badgeProps: {
          showBadge: false
        }
      };
    }

    return props;
  };

  const renderUpgradePopover = (item, modeSpecificProps) => {
    const customTrigger = (
      <MainNavItem
        color={"primary"}
        icon={item.icon}
        size={"large"}
        label={appstr(item.header)}
        dataTestId={`rsb-upgrade-toggle-${item.value}`}
        {...modeSpecificProps}
      />
    );

    if (item.value === enumSidebarMode.grouping.value) {
      const upgradeText = {
        message: appstr("grouping message"),
        template: appstr("grouping template"),
        formatConfig: { bold1: { type: "bold" } },
        replaceDict: { bold1: appstr("compact and concise") }
      };
      return (
        <UpgradePopover
          customTrigger={customTrigger}
          popoverText={upgradeText}
          featureKey={enumFeaturesList.grouping}
        />
      );
    }
    if (item.value === enumSidebarMode.changes.value) {
      const upgradeText = {
        message: appstr("changes message"),
        template: appstr("changes message"),
        formatConfig: { bold1: { type: "bold" } },
        replaceDict: {}
      };
      return (
        <UpgradePopover
          customTrigger={customTrigger}
          popoverText={upgradeText}
          featureKey={enumFeaturesList.trackChanges}
        />
      );
    }
    if (item.value === enumSidebarMode.filters.value) {
      const upgradeText = {
        message: appstr("filters upgrade message"),
        template: appstr("filters upgrade template"),
        formatConfig: { bold1: { type: "bold" } },
        replaceDict: { bold1: appstr("interactive filters") }
      };
      return (
        <UpgradePopover
          customTrigger={customTrigger}
          popoverText={upgradeText}
          featureKey={enumFeaturesList.interactiveFilters}
        />
      );
    }
    if (item.value === enumSidebarMode.highlights.value) {
      const upgradeText = {
        message: appstr("highlights upgrade message"),
        template: appstr("highlights upgrade template"),
        formatConfig: { bold1: { type: "bold" } },
        replaceDict: { bold1: appstr("interactive highlights") }
      };
      return (
        <UpgradePopover
          customTrigger={customTrigger}
          popoverText={upgradeText}
          featureKey={enumFeaturesList.interactiveHighlights}
        />
      );
    }

    return (
      <UpgradePopover
        customTrigger={customTrigger}
        placement="right-start"
        featureKey={enumFeaturesList[item.value] ?? ""}
      />
    );
  };

  const showUpgradePopover = item => {
    if (!permissions.showPricingAndPackaging) return false;
    if (item === enumSidebarMode.filters.value) {
      return permissions.shouldShowUpgradeFilters;
    }
    if (item === enumSidebarMode.highlights.value) {
      return permissions.shouldShowUpgradeHighlights;
    }
    if (item === enumSidebarMode.grouping.value && !getFeatureAccess(enumFeaturesList.grouping)) {
      return true;
    }
    if (
      item === enumSidebarMode.changes.value &&
      !getFeatureAccess(enumFeaturesList.trackChanges)
    ) {
      return true;
    }
    return false;
  };

  const availableModes = getAvailableModes();
  if (availableModes.length === 0) return null;

  //TODO: add clipboard icon for Staging
  const toolbarContent = (
    <Fragment>
      <ToggleButtonGroup
        value={sideBarMode}
        onChange={value => handleSideBarModeChange(value[0])}
        exclusive={true}
        enforceValueSet={false}
        spacing={12}
        orientation={"vertical"}
      >
        {availableModes.map(item => {
          let modeSpecificProps = GetModeSpecificProps(item.value);
          const shouldShowUpgradePopover = showUpgradePopover(item.value);
          if (shouldShowUpgradePopover) {
            return (
              <Fragment key={item.value}>{renderUpgradePopover(item, modeSpecificProps)}</Fragment>
            );
          } else {
            return (
              <ToggleMainNavItem
                key={item.value}
                color={"primary"}
                value={item.value}
                icon={item.icon}
                disabled={!doc}
                size={"large"}
                label={appstr(item.header)}
                tooltipProps={{
                  ...getHoverTipContent(item.hovertip),
                  position: "left"
                }}
                dataTestId={`rsb-toggle-${item.value}`}
                {...modeSpecificProps}
              />
            );
          }
        })}
      </ToggleButtonGroup>
    </Fragment>
  );

  return (
    <aside className="sideContainer" tabIndex="-1">
      <div className={sideBarExpanded ? "side-expanded" : "side"}>
        <RightSidebarComponent
          toolbarProps={{ className: cx("oc-list-container", "ocn-list-scrollbar") }}
          isExpanded={sideBarExpanded}
          headerProps={headerProps}
          toolbarContent={toolbarContent}
          toolbarGapSize={32}
          expandedWidth={420}
        >
          {RenderContent()}
        </RightSidebarComponent>
      </div>
    </aside>
  );
};

const ToggleMainNavItem = props => {
  const { badgeProps, ...rest } = props;
  const { checked: contextChecked, handleToggleChange } = useToggleButtonContext(props.value);

  return (
    <Badge {...badgeProps}>
      <MainNavItem
        {...rest}
        checked={contextChecked}
        onToggle={() => handleToggleChange(props.value)}
      />
    </Badge>
  );
};
